/**
 * Legal on Today — V98-3-4 (PRD G-2 "Legal: determinations and proposals";
 * build-list [R3] "Legal lists in-force gaps"), V98-4 review T4-H1.
 *
 * One number, one meaning — every count is the server's (`/today` ai_legal,
 * the same counter as the grid and the program cards):
 *   header          "AI Regulatory · N undecided in force" (= Operate's first card)
 *   Determinations  "3 open · 2 ready · 1 waiting", "1 in the v4 draft",
 *                   "N reopened by rule changes", proposals awaiting Legal
 *                   (onboarding's included); then "Record decision vX" once
 *                   nothing is open, then "Nothing open · vX recorded".
 *   Non-compliant   open remediation in the program that isn't an undecided
 *                   cell — rows NOT already in Owed (never listed twice), each
 *                   with one action (nudge its owner, or open it).
 * The undecided cells themselves are in Owed ("Decide") — never listed twice.
 */
import { Link } from 'react-router-dom';
import { Button } from '@aigovops/design-system';
import { legalDeterminationsProgram, type LegalTodaySummary, type OwedRow, type ProgramCard } from '@aigovops/core';
import type { BlockerRow } from '../../lib/coverage-api';
import { nudgeMessage, useNudges } from '../../lib/nudge-state';
import { daysBetween, fmtDay, itemHref, whenOf } from '../../lib/today-model';
import { Chip, domId } from './shared';

export interface InForceGap { key: string; title: string; since: string | null; owner: string; ownerId: string | null; ref: OwedRow['ref'] | null }

/**
 * Non-compliant (T4-H1): open remediation in the AI Regulatory program that is not an undecided cell
 * (`determination:` keys are the cells — they're in Owed as "Decide"), minus anything already in Owed.
 */
export function inForceGaps(aiProfileId: string | null, owed: readonly OwedRow[], blockers: readonly BlockerRow[] | undefined): InForceGap[] {
  if (!aiProfileId) return [];
  const inOwed = new Set(owed.map((r) => r.key));
  return (blockers ?? []).filter((b) => b.next_action === 'remediate' && b.programs.includes(aiProfileId) && !b.item_key.startsWith('determination:') && !inOwed.has(b.item_key))
    .map((b) => ({ key: b.item_key, title: b.title, since: whenOf(b), owner: b.holders.join(', ') || 'Unassigned', ownerId: b.holder_ids[0] ?? null, ref: b.ref ?? null }));
}

const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

export function LegalCard({ ai, gaps, prefix, todayDay, slug, canNudge, focusLater, announce, summary = null }: {
  ai: ProgramCard | null; gaps: readonly InForceGap[]; prefix: string; todayDay: string; slug: string;
  /** V98-4-5: open determinations / draft / record state (null when the viewer isn't the legal reviewer). */
  summary?: LegalTodaySummary | null;
  /** Nudging is a lead / admin action (server-enforced); others open the item. */
  canNudge: boolean;
  focusLater: (id: string) => void; announce: (m: string) => void;
}) {
  const nudges = useNudges(slug);
  const send = async (g: InForceGap) => {
    const s = await nudges.send(g.key, { user_id: g.ownerId, name: g.owner });
    announce(nudgeMessage(s, g.owner));
    focusLater(domId('gap', g.key));
  };
  const sentText = (k: string): string | null => {
    const s = nudges.state[k];
    return !s ? null : s.kind === 'sent' ? `Nudged · ${s.to}` : s.kind === 'already' ? 'Nudged today' : s.kind === 'away' ? `Owner away until ${fmtDay(s.until)}` : s.text;
  };
  // The program legal determinations live in (manifest `features.legalDeterminations`: AI Regulatory).
  const legalProgram = ai?.program ?? legalDeterminationsProgram() ?? '';
  const scope = `${prefix}/programs/${legalProgram}/scope`;
  const fmtY = (d: string) => fmtDay(d, true);
  const counts = summary ?? null;
  const undecided = counts?.undecided_in_force ?? ai?.ai_legal?.undecided_in_force ?? 0;
  const nonCompliant = counts?.non_compliant ?? ai?.ai_legal?.non_compliant ?? gaps.length;
  const inOwed = Math.max(0, nonCompliant - gaps.length);
  const proposals = counts?.proposals_open ?? 0;
  return (
    <section className="gp-sec" aria-labelledby="legal-h">
      <div className="gp-sec-head">
        <h2 id="legal-h" className="gp-sec-h">Legal</h2>
        <span className="gp-sec-count">{ai ? <>AI Regulatory · <b>{undecided}</b> undecided in force</> : 'AI Regulatory'}</span>
      </div>
      <div className="gp-panel">
        <h3>Determinations</h3>
        {!ai || !counts ? (
          <div className="gp-actions"><Link className="gp-linkbtn gp-linkbtn--compact" to={scope}>{ai ? 'Open AI Regulatory scope' : 'Set up AI Regulatory'}</Link></div>
        ) : counts.open > 0 ? (
          <>
            {/* The chips add up: open = ready + waiting. */}
            <p className="gp-ow-meta">
              <Chip text={`${counts.open} open · ${counts.ready} ready · ${counts.waiting_on_owners} waiting`} tone="amber" />
              {counts.decided_in_draft ? <Chip text={`${counts.decided_in_draft} in the v${counts.draft_version ?? ''} draft`} tone="ok" /> : null}
              {counts.rule_changed ? <Chip text={`${counts.rule_changed} reopened by rule change${counts.rule_changed === 1 ? '' : 's'}`} tone="amber" /> : null}
            </p>
            <div className="gp-actions"><Link className="gp-linkbtn gp-linkbtn--compact" to={scope}>Decide in AI Regulatory</Link></div>
          </>
        ) : counts.can_record && counts.draft_version ? (
          <>
            <p className="gp-state gp-state--ok">{`All ${counts.decided_in_draft} in the v${counts.draft_version} draft`}</p>
            <div className="gp-actions"><Link className="gp-linkbtn gp-linkbtn--compact" to={`${scope}?record=1`}>{`Record decision v${counts.draft_version}`}</Link></div>
          </>
        ) : (
          <>
            <p className="gp-state gp-state--ok">{counts.current_version ? `Nothing open · v${counts.current_version} recorded${counts.review_by ? ` · re-review by ${fmtY(counts.review_by)}` : ''}` : 'Nothing open'}</p>
            <div className="gp-actions"><Link className="gp-linkbtn gp-linkbtn--compact" to={scope}>Open AI Regulatory scope</Link></div>
          </>
        )}
        {proposals ? (
          <p className="gp-ow-meta">
            <Chip text={`${plural(proposals, 'proposal')} awaiting you`} tone="amber" />
            <Link to={`${scope}?record=1`}>Review in the draft</Link>
          </p>
        ) : null}
      </div>
      <div className="gp-panel">
        <h3>{`Non-compliant · ${nonCompliant}`}</h3>
        {gaps.length ? (
          <ul className="gp-owed-list" aria-label="Non-compliant">
            {gaps.map((g) => {
              const days = g.since && g.since < todayDay ? daysBetween(g.since, todayDay) : 0;
              const sent = sentText(g.key);
              return (
                <li key={g.key} className="gp-ow">
                  <span className="gp-ow-main">
                    <span className="gp-ow-line">
                      {g.ref ? <Link className="gp-ow-t" to={itemHref(prefix, g.ref)}>{g.title}</Link> : <span className="gp-ow-t">{g.title}</span>}
                    </span>
                    <span className="gp-ow-meta">
                      {days > 0 ? <Chip text={`Since ${fmtDay(g.since)} · ${days} days`} tone="amber" /> : <Chip text={g.since ? `Due ${fmtDay(g.since)}` : 'No date'} />}
                      <span>{g.owner}</span>
                    </span>
                  </span>
                  <span className="gp-ow-act" id={domId('gap', g.key)} tabIndex={-1}>
                    {sent ? <Chip text={sent} tone="lock" />
                      : canNudge && g.ownerId ? <Button compact aria-label={`Nudge ${g.owner} · ${g.title}`} onClick={() => void send(g)}>Nudge</Button>
                      : g.ref ? <Link className="gp-linkbtn gp-linkbtn--compact" to={itemHref(prefix, g.ref)} aria-label={`Open · ${g.title}`}>Open</Link>
                      : <Link className="gp-linkbtn gp-linkbtn--compact" to={`${prefix}/programs/${legalProgram}/operate`} aria-label={`Open Operate · ${g.title}`}>Open Operate</Link>}
                  </span>
                </li>
              );
            })}
          </ul>
        ) : null}
        {inOwed ? <p className="gp-dim">{`${inOwed} in your list above`}</p> : !gaps.length ? <p className="gp-dim">Nothing non-compliant</p> : null}
        <div className="gp-actions"><Link className="gp-linkbtn gp-linkbtn--compact" to={`${prefix}/programs/${legalProgram}/operate`}>Open Operate</Link></div>
      </div>
    </section>
  );
}
